'use client';

import { useEffect, useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import {
  UTM_KEYS,
  CLICK_ID_KEYS,
  EMAIL_CAMPAIGN_KEYS,
  STORAGE_KEYS,
  FIRST_TOUCH_EXPIRY_MS,
} from '@/lib/attribution';
import type { UtmParams, ClickIds, EmailCampaignIds } from '@/lib/attribution';

// Re-export types for backwards compatibility
export type { UtmParams, ClickIds };

// ---------------------------------------------------------------------------
// Storage helpers
// ---------------------------------------------------------------------------

function readJson<T>(storage: Storage, key: string): T | null {
  try {
    const raw = storage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(storage: Storage, key: string, value: unknown) {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage may be unavailable in private browsing
  }
}

function isFirstTouchExpired(): boolean {
  try {
    const expiry = localStorage.getItem(STORAGE_KEYS.FIRST_TOUCH_EXPIRY);
    if (!expiry) return true;
    return Date.now() > Number(expiry);
  } catch {
    return true;
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Captures UTM parameters, click IDs, email campaign identifiers,
 * referrer, and landing page from the URL on first visit.
 *
 * Storage strategy:
 *  - **First-touch** (localStorage, 30-day expiry): preserved across sessions
 *  - **Last-touch** (sessionStorage): current session only, overwritten on each new UTM visit
 *
 * Usage:
 *   const { getUtmData } = useUtmCapture();
 *   const attribution = getUtmData();
 */
export function useUtmCapture() {
  const searchParams = useSearchParams();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // ------------------------------------------------------------------
    // Capture referrer and landing page (once per session)
    // ------------------------------------------------------------------
    if (!sessionStorage.getItem(STORAGE_KEYS.LANDING_PAGE)) {
      sessionStorage.setItem(STORAGE_KEYS.LANDING_PAGE, window.location.href);
    }
    if (!sessionStorage.getItem(STORAGE_KEYS.REFERRER) && document.referrer) {
      sessionStorage.setItem(STORAGE_KEYS.REFERRER, document.referrer);
    }

    if (!searchParams) return;

    // ------------------------------------------------------------------
    // UTM Parameters
    // ------------------------------------------------------------------
    const utmParams: UtmParams = {};
    let hasUtm = false;
    for (const key of UTM_KEYS) {
      const val = searchParams.get(key);
      if (val) {
        utmParams[key] = val.toLowerCase();
        hasUtm = true;
      }
    }

    if (hasUtm) {
      // Last-touch: always overwrite in sessionStorage
      writeJson(sessionStorage, STORAGE_KEYS.UTM_LAST_TOUCH, utmParams);

      // First-touch: only write if expired or absent
      if (isFirstTouchExpired()) {
        writeJson(localStorage, STORAGE_KEYS.UTM_FIRST_TOUCH, utmParams);
        try {
          localStorage.setItem(
            STORAGE_KEYS.FIRST_TOUCH_EXPIRY,
            String(Date.now() + FIRST_TOUCH_EXPIRY_MS),
          );
        } catch {
          // localStorage unavailable
        }
      }
    }

    // ------------------------------------------------------------------
    // Click IDs (gclid, fbclid, msclkid, ttclid, li_fat_id)
    // ------------------------------------------------------------------
    const clickIds: ClickIds = {};
    let hasClickId = false;
    for (const key of CLICK_ID_KEYS) {
      const val = searchParams.get(key);
      if (val) {
        clickIds[key] = val;
        hasClickId = true;
      }
    }
    if (hasClickId) {
      writeJson(sessionStorage, STORAGE_KEYS.CLICK_IDS, clickIds);
    }

    // ------------------------------------------------------------------
    // Email Campaign Identifiers (eid, uid, email_campaign_id)
    // ------------------------------------------------------------------
    const emailIds: EmailCampaignIds = {};
    let hasEmailId = false;
    for (const key of EMAIL_CAMPAIGN_KEYS) {
      const val = searchParams.get(key);
      if (val) {
        emailIds[key] = val;
        hasEmailId = true;
      }
    }
    if (hasEmailId) {
      writeJson(sessionStorage, STORAGE_KEYS.EMAIL_CAMPAIGN, emailIds);
    }
  }, [searchParams]);

  const getUtmData = useCallback((): UtmParams & ClickIds => {
    if (typeof window === 'undefined') return {};
    const utm = readJson<UtmParams>(sessionStorage, STORAGE_KEYS.UTM_LAST_TOUCH) ?? {};
    const clicks = readJson<ClickIds>(sessionStorage, STORAGE_KEYS.CLICK_IDS) ?? {};
    return { ...utm, ...clicks };
  }, []);

  return { getUtmData };
}

/**
 * Non-hook version for use outside React components (e.g. in analyticsEvents).
 * Reads the stored UTM data from sessionStorage.
 */
export function getStoredUtmData(): UtmParams & ClickIds {
  if (typeof window === 'undefined') return {};
  const utm = readJson<UtmParams>(sessionStorage, STORAGE_KEYS.UTM_LAST_TOUCH) ?? {};
  const clicks = readJson<ClickIds>(sessionStorage, STORAGE_KEYS.CLICK_IDS) ?? {};
  return { ...utm, ...clicks };
}
